import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { ArrowUpRightIcon, ArrowDownRightIcon, PlayIcon } from 'lucide-react';
const areaData = [{
  day: 'Mon',
  spend: 4000,
  revenue: 12400
}, {
  day: 'Tue',
  spend: 3000,
  revenue: 13980
}, {
  day: 'Wed',
  spend: 2000,
  revenue: 9800
}, {
  day: 'Thu',
  spend: 2780,
  revenue: 19080
}, {
  day: 'Fri',
  spend: 1890,
  revenue: 14800
}, {
  day: 'Sat',
  spend: 2390,
  revenue: 18800
}, {
  day: 'Sun',
  spend: 3490,
  revenue: 23000
}];
const barData = [{
  name: 'Google',
  value: 45
}, {
  name: 'Meta',
  value: 30
}, {
  name: 'TikTok',
  value: 15
}, {
  name: 'LinkedIn',
  value: 10
}];
const COLORS = ['#00D4AA', '#6366F1', '#F59E0B', '#EF4444'];
export function HeroSection() {
  return <section className="pt-32 pb-20 px-6 min-h-screen flex items-center" data-id="element-151">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-152">
        {/* Left Content */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="space-y-8" data-id="element-153">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy-800 border border-navy-700 text-xs font-mono text-accent-teal" data-id="element-154">
            <span className="relative flex h-2 w-2" data-id="element-155">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-teal opacity-75" data-id="element-156"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-teal" data-id="element-157"></span>
            </span>
            System Online v2.4.1
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight" data-id="element-158">
            Your PPC Data,
            <br data-id="element-159" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-teal to-accent-indigo" data-id="element-160">
              Finally Decoded.
            </span>
          </h1>

          <p className="text-lg text-gray-400 leading-relaxed max-w-xl" data-id="element-161">
            Roguehaven unifies fragmented ad platform data into one command
            center. Real-time dashboards, automated reports, and anomaly
            detection — built for agencies that refuse to fly blind.
          </p>

          <div className="flex flex-wrap items-center gap-4" data-id="element-162">
            <button className="bg-accent-teal text-navy-900 hover:bg-accent-teal/90 transition-colors px-6 py-3.5 rounded-md font-semibold flex items-center gap-2" data-id="element-163">
              Launch Your Dashboard
              <ArrowUpRightIcon className="w-4 h-4" data-id="element-164" />
            </button>
            <button className="bg-transparent text-white border border-navy-700 hover:bg-navy-800 transition-colors px-6 py-3.5 rounded-md font-medium flex items-center gap-2" data-id="element-165">
              <PlayIcon className="w-4 h-4" data-id="element-166" />
              Watch the Walkthrough
            </button>
          </div>
        </motion.div>

        {/* Right Dashboard Mockup */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative" data-id="element-167">
          {/* Main Chart Widget */}
          <div className="bg-navy-800 border border-navy-700 rounded-xl p-6 shadow-2xl relative z-10" data-id="element-168">
            <div className="flex justify-between items-center mb-6" data-id="element-169">
              <div data-id="element-170">
                <h3 className="text-white font-medium" data-id="element-171">Ad Spend vs Revenue</h3>
                <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-172">
                  Last 7 Days
                </p>
              </div>
              <div className="flex gap-3 text-xs font-mono" data-id="element-173">
                <div className="flex items-center gap-1" data-id="element-174">
                  <div className="w-2 h-2 rounded-full bg-accent-teal" data-id="element-175"></div>
                  Revenue
                </div>
                <div className="flex items-center gap-1" data-id="element-176">
                  <div className="w-2 h-2 rounded-full bg-accent-amber" data-id="element-177"></div>
                  Spend
                </div>
              </div>
            </div>
            <div className="h-64 w-full" data-id="element-178">
              <ResponsiveContainer width="100%" height="100%" data-id="element-179">
                <AreaChart data={areaData} margin={{
                top: 0,
                right: 0,
                left: -20,
                bottom: 0
              }} data-id="element-180">
                  <defs data-id="element-181">
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1" data-id="element-182">
                      <stop offset="5%" stopColor="#00D4AA" stopOpacity={0.3} data-id="element-183" />
                      <stop offset="95%" stopColor="#00D4AA" stopOpacity={0} data-id="element-184" />
                    </linearGradient>
                    <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1" data-id="element-185">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} data-id="element-186" />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} data-id="element-187" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2536" vertical={false} data-id="element-188" />
                  <XAxis dataKey="day" stroke="#4B5563" fontSize={10} tickLine={false} axisLine={false} data-id="element-189" />
                  <YAxis stroke="#4B5563" fontSize={10} tickLine={false} axisLine={false} tickFormatter={value => `€${value / 1000}k`} data-id="element-190" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#131825',
                  borderColor: '#1E2536',
                  color: '#fff',
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '12px'
                }} itemStyle={{
                  color: '#fff'
                }} data-id="element-191" />
                  <Area type="monotone" dataKey="revenue" stroke="#00D4AA" strokeWidth={2} fillOpacity={1} fill="url(#colorRev)" data-id="element-192" />
                  <Area type="monotone" dataKey="spend" stroke="#F59E0B" strokeWidth={2} fillOpacity={1} fill="url(#colorSpend)" data-id="element-193" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Floating KPI Cards */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="absolute -bottom-6 -left-6 bg-navy-800 border border-navy-700 rounded-lg p-4 shadow-xl w-48 z-20" data-id="element-194">
            <p className="text-xs text-gray-400 mb-1" data-id="element-195">ROAS</p>
            <div className="flex items-end justify-between" data-id="element-196">
              <span className="text-2xl font-mono text-white font-bold" data-id="element-197">
                4.7x
              </span>
              <div className="flex items-center text-accent-teal text-xs font-mono bg-accent-teal/10 px-1.5 py-0.5 rounded" data-id="element-198">
                <ArrowUpRightIcon className="w-3 h-3 mr-0.5" data-id="element-199" />
                +12.3%
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="absolute -top-6 -right-6 bg-navy-800 border border-navy-700 rounded-lg p-4 shadow-xl w-48 z-20" data-id="element-200">
            <p className="text-xs text-gray-400 mb-1" data-id="element-201">CPA</p>
            <div className="flex items-end justify-between" data-id="element-202">
              <span className="text-2xl font-mono text-white font-bold" data-id="element-203">
                €14.20
              </span>
              <div className="flex items-center text-accent-red text-xs font-mono bg-accent-red/10 px-1.5 py-0.5 rounded" data-id="element-204">
                <ArrowDownRightIcon className="w-3 h-3 mr-0.5" data-id="element-205" />
                -8.1%
              </div>
            </div>
          </motion.div>

          {/* Mini Bar Chart Widget */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="absolute -bottom-12 -right-4 bg-navy-800 border border-navy-700 rounded-lg p-4 shadow-xl w-56 z-20 hidden md:block" data-id="element-206">
            <p className="text-xs text-gray-400 mb-3" data-id="element-207">Channel Breakdown</p>
            <div className="h-24 w-full" data-id="element-208">
              <ResponsiveContainer width="100%" height="100%" data-id="element-209">
                <BarChart data={barData} layout="vertical" margin={{
                top: 0,
                right: 0,
                left: -20,
                bottom: 0
              }} data-id="element-210">
                  <XAxis type="number" hide data-id="element-211" />
                  <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={10} tickLine={false} axisLine={false} data-id="element-212" />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={8} data-id="element-213">
                    {barData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-214" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}